import { ActionFunctionArgs, LoaderFunctionArgs, redirect } from "react-router-dom";
import { changeWeek, myAxios, MyDate, standbyWeeks, weeknum } from "src/components/common/functions";

export type StandbyWeekData = {
    data?: any[];
    status?: string;
    submitTime?: string;
    id?: string | null;
    billableId?: string | null;
    otherNotes?: string;
    region?: string;
};

export type StandbyRow = {
    id: string;
    email: string;
    userName: string;
    lanId?: string;
    phoneNumber?: string;
    street?: string;
    residence?: string;
    residenceState?: string;
    zipCode?: string;
    outside: string;
    hireDate: string;
    quitDate?: string;
    weekData: StandbyWeekData;
};

function parseWeekData(raw: unknown): StandbyWeekData {
    if (typeof raw === "string" && raw) {
        try {
            return JSON.parse(raw);
        } catch {
            return {};
        }
    }
    return raw && typeof raw === "object" ? raw as StandbyWeekData : {};
}

export async function allStandbyListLoader({ params }: LoaderFunctionArgs) {
    const fallbackWeek = weeknum(new MyDate().s());
    const selectedWeek = params.week && standbyWeeks.includes(params.week) ? params.week : fallbackWeek;
    if (params.week !== selectedWeek) throw redirect(`/OutsideStatusStandby/${selectedWeek}`);

    const formData = new FormData();
    formData.append("week", selectedWeek);
    const res = await myAxios({ fileName: "outside/standby/readTimeCards.php", formData });
    if (!res) throw redirect("/");

    const rows: StandbyRow[] = Array.isArray(res.data) ? res.data.map((row: any) => ({
        id: String(row.id ?? ""),
        email: row.email ?? "",
        userName: row.userName ?? "",
        lanId: row.lanId ?? "",
        phoneNumber: row.phoneNumber ?? "",
        street: row.street ?? "",
        residence: row.residence ?? "",
        residenceState: row.residenceState ?? "",
        zipCode: row.zipCode ?? "",
        outside: row.outside ?? "",
        hireDate: row.hireDate ?? "",
        quitDate: row.quitDate ?? "",
        weekData: parseWeekData(row[selectedWeek]),
    })) : [];

    return { week: selectedWeek, rows };
}

export async function allStandbyDetailLoader({ params }: LoaderFunctionArgs) {
    const fallbackWeek = weeknum(new MyDate().s());
    const selectedWeek = params.week && standbyWeeks.includes(params.week) ? params.week : fallbackWeek;
    const userId = params.userId ?? "";
    if (!userId) throw redirect(`/OutsideStatusStandby/${selectedWeek}`);
    if (params.week !== selectedWeek) throw redirect(`/OutsideStatusStandby/${selectedWeek}/${userId}`);

    const prevWeek = changeWeek(selectedWeek, -1);
    const formData = new FormData();
    formData.append("week", selectedWeek);
    formData.append("userId", userId);
    if (prevWeek !== "w2023-28") formData.append("prevWeek", prevWeek);
    const res = await myAxios({ fileName: "outside/standby/readTimeCard.php", formData });
    if (!res?.data) throw redirect(`/OutsideStatusStandby/${selectedWeek}`);

    return {
        week: selectedWeek,
        form: parseWeekData(res.data[selectedWeek]),
        prevForm: prevWeek !== "w2023-28" ? parseWeekData(res.data[prevWeek]) : undefined,
        basePath: `/OutsideStatusStandby/${selectedWeek}/${userId}`,
        admin: true,
        selectedUser: {
            id: res.data.id ?? userId,
            email: res.data.email ?? "",
            userName: res.data.userName ?? "",
            lanId: res.data.lanId ?? "",
            street: res.data.street ?? "",
            residence: res.data.residence ?? "",
            residenceState: res.data.residenceState ?? "",
            zipCode: res.data.zipCode ?? "",
            phoneNumber: res.data.phoneNumber ?? "",
        },
    };
}

export async function allStandbySwitchWeekAction({ params, request }: ActionFunctionArgs) {
    const requestForm = await request.formData();
    const formData = new FormData();
    formData.append("userId", params.userId ?? "");
    formData.append("fromWk", params.week ?? "");
    formData.append("toWk", String(requestForm.get("toWk") ?? ""));
    const res = await myAxios({ fileName: "outside/standby/switchWeek.php", formData });
    if (!res) return null;
    return redirect(`/OutsideStatusStandby/${params.week}/${params.userId}`);
}
